/**
 * Product PDP helpers for Simple Bundles Pack products:
 * - Flavor links + Pack Size cards bound to native variants
 * - Prices / subscribe / ATC follow selected Pack variant
 */
(function () {
  function getMoneyFormat(root) {
    const picker = getPicker(root);
    const fromPicker = picker?.dataset?.moneyFormat;
    if (fromPicker) return fromPicker;
    const fromJson = parseJsonScript(picker, '[data-unit-pricing]')?.money_format;
    return fromJson || window.theme?.moneyFormat || window.shopMoneyFormat || '${{amount}}';
  }

  function formatMoney(cents, root) {
    if (cents == null || cents === '') return '';
    if (typeof cents === 'string' && cents.trim().startsWith('$')) return cents.trim();
    const value = Number(cents);
    if (Number.isNaN(value)) return String(cents);

    const format = getMoneyFormat(root);
    const amount = (Math.round(value) / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(value / 100));
    const amountComma = amount.replace('.', ',');

    if (format && format.includes('{{')) {
      return format
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount.replace('.', "'"))
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    try {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(value, format || '${{amount}}');
      }
    } catch (e) {
      /* fall through */
    }
    return `$${amount}`;
  }

  function getProductRoot(fromEl) {
    return (fromEl && fromEl.closest && fromEl.closest('product-info')) || document.querySelector('product-info');
  }

  function parseJsonScript(root, selector) {
    try {
      return JSON.parse(root?.querySelector(selector)?.textContent || 'null');
    } catch (e) {
      return null;
    }
  }

  function savePercent(price, compare) {
    const current = Number(price) || 0;
    const original = Number(compare) || 0;
    if (original <= current || original <= 0) return 0;
    return Math.round(((original - current) * 100) / original);
  }

  function getPicker(root) {
    return root?.querySelector('.product-flavor-pack-picker') || root?.querySelector('variant-selects');
  }

  function getPurchaseOptions(root) {
    return root?.querySelector('.pdp-purchase-options') || null;
  }

  function getPurchaseMode(root) {
    const checked = root?.querySelector('[data-purchase-mode]:checked');
    return checked?.value || 'subscribe';
  }

  function getSelectedPlanId(root) {
    return root?.querySelector('[data-selling-plan-select]')?.value || '';
  }

  function getFallbackSave(root) {
    const widget = getPurchaseOptions(root);
    return Number(widget?.dataset.fallbackSave) || 25;
  }

  function getServingsPerJar(root, variant) {
    const widget = getPurchaseOptions(root);
    const fromWidget = Number(widget?.dataset.servingsPerJar) || 0;
    const fromVariant = Number(variant?.servings) || 0;
    return fromWidget || fromVariant || 4;
  }

  function renderServingPrice(wrap, amountAttr, cents, root) {
    if (!wrap) return;
    const value = Math.max(0, Math.round(Number(cents) || 0));
    wrap.hidden = value <= 0;
    if (value <= 0) {
      wrap.textContent = '';
      return;
    }
    const formatted = formatMoney(value, root);
    wrap.hidden = false;
    wrap.innerHTML = `/ <span ${amountAttr}></span> per serving`;
    const amountEl = wrap.querySelector(`[${amountAttr}]`);
    if (amountEl) amountEl.textContent = formatted;
  }

  function applyFallbackDiscount(oneTimePrice, percent) {
    const price = Number(oneTimePrice) || 0;
    const save = Number(percent) || 0;
    if (price <= 0 || save <= 0) return price;
    return Math.round((price * (100 - save)) / 100);
  }

  function getUnitPricing(root) {
    const picker = getPicker(root);
    return parseJsonScript(picker, '[data-unit-pricing]');
  }

  function getVariants(unitPricing) {
    if (Array.isArray(unitPricing?.variants) && unitPricing.variants.length) {
      return unitPricing.variants;
    }
    if (unitPricing?.id) return [unitPricing];
    return [];
  }

  function getSelectedVariantId(root) {
    const checked = root?.querySelector('.pack-size-card__input:checked');
    const fromInput = Number(checked?.value || checked?.dataset?.variantId);
    if (fromInput > 0) return fromInput;
    const picker = getPicker(root);
    return Number(picker?.dataset.defaultVariantId) || 0;
  }

  function getVariantById(unitPricing, variantId) {
    const variants = getVariants(unitPricing);
    const match = variants.find((item) => String(item.id) === String(variantId));
    if (match) return match;
    return variants[0] || unitPricing || null;
  }

  function getSelectedVariant(root, unitPricing) {
    return getVariantById(unitPricing, getSelectedVariantId(root));
  }

  function allocationAppId(item) {
    return String(item?.app_id || '').toLowerCase();
  }

  function isAppstleAllocation(item) {
    return allocationAppId(item).includes('appstle');
  }

  function isSealAllocation(item) {
    return allocationAppId(item).includes('seal');
  }

  function getVariantAllocations(variant) {
    const allocations = Array.isArray(variant?.selling_plan_allocations)
      ? variant.selling_plan_allocations
      : [];
    const appstle = allocations.filter(isAppstleAllocation);
    if (appstle.length) return appstle;
    const nonSeal = allocations.filter((item) => !isSealAllocation(item));
    return nonSeal.length ? nonSeal : allocations;
  }

  function getAllocation(variant, planId) {
    const allocations = getVariantAllocations(variant);
    if (!allocations.length) return null;
    if (planId) {
      const match = allocations.find((item) => String(item.selling_plan_id) === String(planId));
      if (match) return match;
    }
    return allocations[0];
  }

  function planFrequencyKey(name) {
    const text = String(name || '').toLowerCase().replace(/\s+/g, ' ');
    if (/(2|two)\s*weeks?/.test(text)) return '2w';
    if (/(3|three)\s*months?/.test(text)) return '3m';
    if (/(2|two)\s*months?/.test(text)) return '2m';
    if (text.includes('week')) return '1w';
    if (text.includes('month')) return '1m';
    return text;
  }

  function syncPlanSelect(root, variant) {
    const select = root?.querySelector('[data-selling-plan-select]');
    if (!select) return;

    const allocations = getVariantAllocations(variant);
    if (!allocations.length) return;

    const previousId = select.value;
    const previousKey = planFrequencyKey(select.options[select.selectedIndex]?.textContent);
    const next = [];
    const seen = new Set();
    allocations.forEach((alloc) => {
      const id = String(alloc.selling_plan_id || '');
      if (!id || seen.has(id)) return;
      seen.add(id);
      next.push(alloc);
    });

    const unchanged =
      next.length === select.options.length &&
      next.every((alloc, index) => String(select.options[index]?.value) === String(alloc.selling_plan_id));

    if (!unchanged) {
      select.innerHTML = '';
      next.forEach((alloc) => {
        const option = document.createElement('option');
        option.value = String(alloc.selling_plan_id);
        option.textContent = alloc.selling_plan_name || 'Subscribe';
        option.dataset.planSave = String(alloc.adjustment_percent || 0);
        select.appendChild(option);
      });
    }

    const byId = [...select.options].find((option) => option.value === previousId);
    const byFreq = [...select.options].find(
      (option) => planFrequencyKey(option.textContent) === previousKey
    );
    select.value = byId?.value || byFreq?.value || select.options[0]?.value || '';
  }

  function getSubscribeSavePercent(variant, root) {
    const widget = getPurchaseOptions(root);
    const allocation = getAllocation(variant, getSelectedPlanId(root));
    const selectedOption = widget?.querySelector('[data-selling-plan-select] option:checked');
    const planSave = Number(selectedOption?.dataset.planSave) || 0;
    const allocationSave = Number(allocation?.adjustment_percent) || 0;
    const appSave = Number(widget?.dataset.appSavePercent) || 0;
    return planSave || allocationSave || appSave || getFallbackSave(root);
  }

  function getSubscribeTotal(variant, root) {
    const oneTime = Number(variant?.price) || 0;
    if (oneTime <= 0) return 0;

    const allocation = getAllocation(variant, getSelectedPlanId(root));
    const savePercentValue = getSubscribeSavePercent(variant, root);

    if (allocation && allocation.price != null) {
      const allocPrice = Number(allocation.price);
      if (allocPrice > 0 && allocPrice < oneTime) return allocPrice;
      if (allocPrice > 0 && allocPrice !== oneTime) return allocPrice;
    }

    if (savePercentValue > 0) {
      return applyFallbackDiscount(oneTime, savePercentValue);
    }

    return oneTime;
  }

  function jarCountFromVariant(variant) {
    const fromData = Number(variant?.jar_count);
    if (fromData > 0) return fromData;
    const label = String(variant?.option || variant?.title || '');
    const match = label.match(/(\d+)/);
    return match ? Number(match[1]) : 1;
  }

  function setFormVariantId(root, variantId) {
    const id = Number(variantId) || 0;
    if (!id) return;
    root?.querySelectorAll('input.product-variant-id, input[name="id"]').forEach((input) => {
      input.value = String(id);
      input.disabled = false;
    });
  }

  function setFormQuantity(root, qty) {
    const quantity = Math.max(1, Number(qty) || 1);
    root?.querySelectorAll('input[name="quantity"], [data-pack-quantity]').forEach((input) => {
      input.value = String(quantity);
    });
  }

  function syncPackFormFields(root) {
    const variantId = getSelectedVariantId(root);
    setFormVariantId(root, variantId);
    setFormQuantity(root, 1);
  }

  function setText(el, value) {
    if (el) el.textContent = value;
  }

  function updateCardPricing(card, price, compare, save, root) {
    const priceEl = card.querySelector('.pack-size-card__price');
    const compareEl = card.querySelector('.pack-size-card__compare');
    const saveEl = card.querySelector('.pack-size-card__save');

    setText(priceEl, formatMoney(price, root));

    if (compareEl) {
      if (compare > price) {
        compareEl.hidden = false;
        compareEl.textContent = formatMoney(compare, root);
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
    }

    if (saveEl) {
    const includesShipping = false;
      let saveText = '';
      if (save > 0 && includesShipping) {
        saveText = `Save ${save}% + Free Shipping`;
      } else if (save > 0) {
        saveText = `Save ${save}%`;
      } else if (includesShipping) {
        saveText = 'Free Shipping';
      }

      if (saveText) {
        saveEl.hidden = false;
        saveEl.textContent = saveText;
      } else {
        saveEl.hidden = true;
        saveEl.textContent = '';
      }
    }

    card.dataset.price = String(price);
    card.dataset.compareAt = String(compare);
    card.dataset.savePercent = String(save);
  }

  function packSubscribeSavePercent(jarCount, fallback) {
    const qty = Number(jarCount) || 0;
    if (qty === 4) return 15;
    if (qty === 8) return 20;
    if (qty === 12) return 25;
    return Math.max(0, Number(fallback) || 0);
  }

  function variantTotals(variant, root) {
    const oneTime = Number(variant?.price) || 0;
    const compareAt = Number(variant?.compare_at_price) || 0;
    const original = compareAt > oneTime ? compareAt : oneTime;
    const subscribePrice = getSubscribeTotal(variant, root);
    const allocation = getAllocation(variant, getSelectedPlanId(root));
    const planSave = Number(allocation?.adjustment_percent) || 0;
    const oneTimeSave = savePercent(oneTime, original);
    const subscribeSave = savePercent(subscribePrice, original);
    return {
      oneTime,
      compareAt: original,
      subscribePrice,
      oneTimeSave,
      subscribeSave,
      planSave,
      jarCount: jarCountFromVariant(variant),
    };
  }

  function getPackCardDisplayTotals(totals, mode) {
    if (mode === 'subscribe') {
      const compare = totals.compareAt > totals.subscribePrice ? totals.compareAt : 0;
      return {
        price: totals.subscribePrice,
        compare,
        // Always show marketed pack % (15/20/25), not Appstle's live adjustment.
        save: packSubscribeSavePercent(totals.jarCount, totals.subscribeSave),
      };
    }

    return {
      price: totals.oneTime,
      compare: totals.compareAt > totals.oneTime ? totals.compareAt : 0,
      save: totals.oneTimeSave,
    };
  }

  function getSelectedSubscribeDisplayPercent(variant, root) {
    const totals = variantTotals(variant, root);
    return packSubscribeSavePercent(totals.jarCount, getFallbackSave(root));
  }

  const injectedWidgetSelector = [
    '.sealsubs-container',
    '.sealsubs-target-element',
    '[data-seal-extension]',
    '.appstle_subscription_widget',
    '.appstle_sub_widget',
    '.appstle-subscription-widget',
    '[id^="appstle_subscription"]',
    '[class*="appstle_subscription_widget"]',
    '[class*="appstle_sub_widget"]',
  ].join(', ');

  function hideInjectedSubscriptionWidgets(root) {
    root?.querySelectorAll(injectedWidgetSelector).forEach((el) => {
      if (el.closest('.pdp-purchase-options')) return;
      el.style.display = 'none';
      el.querySelectorAll('input, select, textarea').forEach((field) => {
        if (field.hasAttribute('data-selling-plan-input') || field.hasAttribute('data-purchase-mode')) return;
        field.disabled = true;
        if (field.getAttribute('name') === 'selling_plan') field.removeAttribute('name');
      });
    });
    syncSellingPlanInput(root);
  }

  function getProductForm(root) {
    return (
      root?.querySelector('form[data-type="add-to-cart-form"]') ||
      root?.querySelector('product-form form') ||
      null
    );
  }

  function collectSellingPlanFields(root) {
    const fields = new Set();
    const form = getProductForm(root);
    root?.querySelectorAll('[name="selling_plan"], [data-selling-plan-input]').forEach((el) => fields.add(el));
    form?.querySelectorAll('[name="selling_plan"], [data-selling-plan-input]').forEach((el) => fields.add(el));
    const formId = form?.id;
    if (formId) {
      document.querySelectorAll('[name="selling_plan"][form]').forEach((el) => {
        if (el.getAttribute('form') === formId) fields.add(el);
      });
    }
    return [...fields];
  }

  function setSellingPlanFieldActive(field, active, value) {
    if (!field) return;
    if (active && value) {
      field.disabled = false;
      field.setAttribute('name', 'selling_plan');
      field.value = String(value);
    } else {
      field.disabled = true;
      field.value = '';
      field.removeAttribute('name');
    }
  }

  function syncSellingPlanInput(root) {
    if (!root) return;
    const mode = getPurchaseMode(root);
    const planId = getSelectedPlanId(root);
    const ours = root.querySelector('[data-selling-plan-input]');
    const subscribe = mode === 'subscribe' && Boolean(planId);

    collectSellingPlanFields(root).forEach((field) => {
      if (field === ours) return;
      setSellingPlanFieldActive(field, false);
    });

    setSellingPlanFieldActive(ours, subscribe, planId);
  }

  function updateAtcPrice(root, priceText) {
    if (!root || priceText == null || priceText === '') return;
    root.querySelectorAll('[data-atc-price]').forEach((el) => {
      el.textContent = priceText;
    });
    const wrap = root.querySelector('[data-atc-price-wrap]');
    if (wrap) wrap.hidden = false;
  }

  function updateAtcAvailability(root, available) {
    const button = root?.querySelector('[type="submit"][name="add"], .product-form__submit');
    if (!button) return;
    if (available === false) {
      button.setAttribute('disabled', 'disabled');
    } else {
      button.removeAttribute('disabled');
    }
  }

  function updatePurchaseOptions(root, unitPricing, variant) {
    const widget = getPurchaseOptions(root);
    if (!widget || !variant) return;

    syncPlanSelect(root, variant);

    const mode = getPurchaseMode(root);
    const totals = variantTotals(variant, root);
    const servings = Math.max(1, totals.jarCount) * getServingsPerJar(root, variant);
    const maxPercentEl = widget.querySelector('[data-subscribe-max-percent]');
    const subscribeCompareEl = widget.querySelector('[data-subscribe-compare]');
    const subscribePriceEl = widget.querySelector('[data-subscribe-price]');
    const subscribeServingWrap = widget.querySelector('[data-subscribe-serving-wrap]');
    const onetimePriceEl = widget.querySelector('[data-onetime-price]');
    const onetimeServingWrap = widget.querySelector('[data-onetime-serving-wrap]');

    syncSellingPlanInput(root);

    setText(maxPercentEl, String(getSelectedSubscribeDisplayPercent(variant, root)));
    setText(subscribePriceEl, formatMoney(totals.subscribePrice, root));
    setText(onetimePriceEl, formatMoney(totals.oneTime, root));

    if (subscribeCompareEl) {
      if (totals.compareAt > totals.subscribePrice) {
        subscribeCompareEl.hidden = false;
        subscribeCompareEl.textContent = formatMoney(totals.compareAt, root);
      } else {
        subscribeCompareEl.hidden = true;
        subscribeCompareEl.textContent = '';
      }
    }

    if (servings > 0) {
      renderServingPrice(subscribeServingWrap, 'data-subscribe-serving', totals.subscribePrice / servings, root);
      renderServingPrice(onetimeServingWrap, 'data-onetime-serving', totals.oneTime / servings, root);
    } else {
      if (subscribeServingWrap) subscribeServingWrap.hidden = true;
      if (onetimeServingWrap) onetimeServingWrap.hidden = true;
    }

    widget.querySelectorAll('.pdp-purchase-card').forEach((card) => {
      const input = card.querySelector('[data-purchase-mode]');
      card.classList.toggle('is-selected', Boolean(input?.checked));
    });

    const atcPrice = mode === 'subscribe' ? totals.subscribePrice : totals.oneTime;
    updateAtcPrice(root, formatMoney(atcPrice, root));
    updateAtcAvailability(root, variant.available !== false);
  }

  function syncPurchaseModeOnly(root) {
    const widget = getPurchaseOptions(root);
    if (!widget) return;

    const mode = getPurchaseMode(root);
    syncSellingPlanInput(root);

    widget.querySelectorAll('.pdp-purchase-card').forEach((card) => {
      const input = card.querySelector('[data-purchase-mode]');
      card.classList.toggle('is-selected', Boolean(input?.checked));
    });

    const priceEl =
      mode === 'subscribe'
        ? widget.querySelector('[data-subscribe-price]')
        : widget.querySelector('[data-onetime-price]');
    if (priceEl?.textContent) updateAtcPrice(root, priceEl.textContent.trim());
  }

  function updatePackCards(root) {
    const picker = getPicker(root);
    const unitPricing = getUnitPricing(root);
    if (!picker || !unitPricing) {
      syncPurchaseModeOnly(root);
      return null;
    }

    const mode = getPurchaseMode(root);
    syncPackFormFields(root);

    const selected = getSelectedVariant(root, unitPricing);
    syncPlanSelect(root, selected);

    picker.querySelectorAll('.pack-size-card__input').forEach((input) => {
      const card = input.nextElementSibling;
      if (!card?.classList.contains('pack-size-card')) return;
      const variant = getVariantById(unitPricing, input.value || input.dataset.variantId);
      if (!variant) return;
      const totals = variantTotals(variant, root);
      const display = getPackCardDisplayTotals(totals, mode);
      updateCardPricing(card, display.price, display.compare, display.save, root);
    });

    updatePurchaseOptions(root, unitPricing, selected);
    return unitPricing;
  }

  function bindPackQuantity(root) {
    const picker = getPicker(root);
    if (!picker || picker.dataset.qtyBound === 'true') return;
    picker.dataset.qtyBound = 'true';

    picker.addEventListener('change', (event) => {
      if (!event.target.closest('.pack-size-card__input')) return;
      updatePackCards(root);
    });
  }

  function bindPurchaseOptions(root) {
    const widget = getPurchaseOptions(root);
    if (!widget || widget.dataset.bound === 'true') return;
    widget.dataset.bound = 'true';

    const stopSelectHijack = (event) => {
      if (event.target.closest('[data-selling-plan-select], .pdp-purchase-card__delivery')) {
        event.stopPropagation();
      }
    };
    widget.addEventListener('mousedown', stopSelectHijack);
    widget.addEventListener('click', stopSelectHijack);
    widget.addEventListener('pointerdown', stopSelectHijack);

    widget.addEventListener('click', (event) => {
      if (event.target.closest('select, a, button')) return;
      const card = event.target.closest('[data-purchase-card], .pdp-purchase-card');
      const input = card?.querySelector('[data-purchase-mode]');
      if (!input || input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    widget.addEventListener('change', (event) => {
      if (!event.target.closest('[data-purchase-mode], [data-selling-plan-select]')) return;
      updatePackCards(root);
    });
  }

  function neutralizeAppstleHijack() {
    if (document.documentElement.dataset.appstleHijackBound === 'true') return;
    document.documentElement.dataset.appstleHijackBound = 'true';
    const stop = (event) => {
      event.stopImmediatePropagation();
      if (typeof event.preventDefault === 'function') event.preventDefault();
    };
    [
      'AppstleSubscription:SubscriptionWidget:AddToCartIntent',
      'appstle:add-to-cart',
      'appstle:addtocart',
    ].forEach((name) => document.addEventListener(name, stop, true));
  }

  function bindFormSubmit(root) {
    const form = getProductForm(root);
    if (!form || form.dataset.planSubmitBound === 'true') return;
    form.dataset.planSubmitBound = 'true';
    form.addEventListener(
      'submit',
      () => {
        syncPackFormFields(root);
        syncSellingPlanInput(root);
      },
      true
    );
  }

  function bindReviewsScroll(root) {
    const trigger = root?.querySelector('.product-pdp__social-proof');
    if (!trigger || trigger.dataset.reviewsScrollBound === 'true') return;
    trigger.dataset.reviewsScrollBound = 'true';

    trigger.addEventListener('click', (event) => {
      const target = document.querySelector('.reviews-section-wrapper');
      if (!target) return;

      event.preventDefault();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
  }

  function initProductPdp(root) {
    const scope = root && root.querySelector ? root : document;
    const productRoot = getProductRoot(scope.querySelector?.('product-info') || scope);
    neutralizeAppstleHijack();
    bindPackQuantity(productRoot);
    bindPurchaseOptions(productRoot);
    bindFormSubmit(productRoot);
    bindReviewsScroll(productRoot);
    hideInjectedSubscriptionWidgets(productRoot);
    updatePackCards(productRoot);
  }

  document.addEventListener('DOMContentLoaded', () => initProductPdp());
  document.addEventListener('product-info:loaded', (e) => initProductPdp(e.target));

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('product-info') || node.querySelector?.('product-info')) {
          initProductPdp(node);
        }
        if (
          node.matches?.(injectedWidgetSelector) ||
          node.querySelector?.(injectedWidgetSelector)
        ) {
          hideInjectedSubscriptionWidgets(getProductRoot(node));
        }
      });
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
